/* eslint-disable camelcase */
import { ReactNode, useMemo } from 'react'
import {
  Mutation,
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AxiosError } from 'axios'

import { useI18n } from '@ahha/i18n/i18n'
import { TOAST_STATES } from '@ahha/utils/@types/types'

import { setUser } from '@/stores/slices/userSlice'

import { API } from '@/api'
import { ErrorResponse } from '@/api/Base/types'
import { useGlobalNotification } from '@/context/notificationContext'

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: AxiosError<ErrorResponse, any>
  }
}

interface QueryProviderProps {
  children: ReactNode
}

const getServerErrorMessage = ({ response }: AxiosError<ErrorResponse>) => {
  const { status_code, message } = response?.data ?? {}

  return message ?? 'Unknown Error'
}

const toastErrorMessage = (error: Error) => {
}

const mutationErrorLogger = (error: Error, mutation: Mutation, variables: unknown) => {
}

export const QueryProvider = ({ children }: QueryProviderProps) => {
  const { addNotification } = useGlobalNotification()
  const dispatch = useDispatch()
  const { t } = useI18n()

  const queryClient = useMemo(() => new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const { enableErrorToast = true } = query.meta ?? {}
        if (!(error instanceof AxiosError)) {
          return
        }
        // FIXME: axios interceptor에서 처리 가능한지 확인
        if (error.response?.data?.unique_code === 100001) {
          dispatch(setUser({
            token: '',
            user: {
              _id: '',
              email: '',
              name: '',
              lastWorkspaceId: '',
            },
            isSignIn: false,
          }))
          API().setToken('')
          window.location.href = '/login'
        }
        const message = getServerErrorMessage(error)
        console.error(message)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, variables, _, mutation) => {
        const { enableErrorToast = true } = mutation.meta ?? {}
        if (error.response?.status === 403 && error.response?.config.method !== 'get') {
          addNotification({
            title: t('error_notAuthorized_title'),
            status: TOAST_STATES.ERROR,
          })
        }
        if (!(error instanceof AxiosError)) {
          return
        }
        const message = getServerErrorMessage(error)
        console.error(message)
        /* if (enableErrorToast) {
          toastErrorMessage(error)
        } */
      },
    }),
    defaultOptions: {
      queries: {
        // TODO: 바로 바뀌는 화면들을 봐야하는 경우가 더 많은것 같아 임시 주석처리
        // staleTime: 30 * 1000,
        retry: (count, error) => {
          const { status } = error?.response ?? {}

          if (status && status >= 500) {
            return false
          }
          return count < 1
        },
        throwOnError: true,
      },
      mutations: {
        throwOnError: (e) => {
          // NOTE: 라벨러 권한 에러 -> unique code로 변경 필요
          if (e.response?.status === 403 && e.response?.config.method !== 'get') {
            return false
          }
          return true
        },
      },
    },
  }), [])

  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      {children}
    </QueryClientProvider>
  )
}
